/**
 * StructurePanel — pestaña "Estructura" (solo persona jurídica).
 *
 * Combina en una sola pantalla los beneficiarios finales / cadena de control
 * (OwnersPanel) MÁS el mapa jerárquico interactivo de propiedad (OwnershipGraph).
 * Los propietarios se leen con la misma clave de consulta que OwnersPanel, así
 * TanStack Query comparte la caché y no hay doble carga. Cadenas es-PA únicamente.
 */
import { useQuery } from '@tanstack/react-query';
import type { EntityMode } from '@cumpli/shared';
import { api } from '../lib/api';
import { useTenantSettings } from '../lib/hooks';
import { Card, Skeleton } from './ui';
import OwnershipGraph from './OwnershipGraph';
import OwnersPanel from './OwnersPanel';
import TestaferrosPanel from './TestaferrosPanel';

export default function StructurePanel({
  expedienteId,
  companyName,
  companyKind,
}: {
  expedienteId: string;
  companyName: string;
  companyKind: string;
}) {
  const { data: owners, isLoading } = useQuery({
    queryKey: ['expedientes', expedienteId, 'owners'],
    queryFn: () => api.listOwners(expedienteId),
  });

  // El sector del sujeto obligado fija el umbral de beneficiario final (Ley 23).
  const { data: tenantSettings } = useTenantSettings();
  const sector: EntityMode = tenantSettings?.entityMode ?? 'financiero';

  return (
    <div className="grid grid-cols-1 gap-6">
      <Card>
        <h2 className="font-serif text-xl font-medium text-ink">Mapa de propiedad y control</h2>
        <p className="mt-1 text-sm text-ink-soft">
          Cadena completa de la estructura: la empresa en la cima, las personas jurídicas
          intermedias (holdings) descendiendo hasta los beneficiarios finales (personas naturales).
        </p>
        {isLoading ? (
          <Skeleton className="mt-4 h-48 w-full rounded-xl" />
        ) : (
          <OwnershipGraph
            companyName={companyName}
            companyKind={companyKind}
            owners={owners ?? []}
            sector={sector}
          />
        )}
      </Card>

      <OwnersPanel expedienteId={expedienteId} />

      <TestaferrosPanel
        expedienteId={expedienteId}
        owners={owners ?? []}
        isLoadingOwners={isLoading}
        sector={sector}
      />
    </div>
  );
}
